// New Authority Checklist — the day-by-day sequence for a brand-new MC.
// `link` starting with "/" is an in-app route; anything else opens in a new tab.
export const AUTHORITY_PHASES = [
  {
    id: "waiting",
    label: "Phase 1",
    title: "While you wait for your MC#",
    subtitle: "FMCSA's new system takes ~2 weeks to issue the number. None of this needs it — do it now.",
    items: [
      {
        id: "insurance-quotes",
        title: "Get 3+ insurance quotes",
        why: "$1M auto liability + $100k cargo is what most brokers require (FMCSA minimum is $750k). New-venture rates swing thousands between carriers — shop it.",
        cost: "$1,200–2,500/mo typical",
        time: "2–3 days",
        link: "https://www.fmcsa.dot.gov/registration/insurance-filing-requirements",
        linkLabel: "FMCSA insurance requirements",
      },
      {
        id: "clearinghouse",
        title: "Register in the Drug & Alcohol Clearinghouse",
        why: "Register as employer AND driver, then run your own pre-employment query. Brokers and the new-entrant audit both check this.",
        cost: "$1.25 per query",
        time: "20 min",
        link: "https://clearinghouse.fmcsa.dot.gov",
        linkLabel: "clearinghouse.fmcsa.dot.gov",
      },
      {
        id: "consortium",
        title: "Join a random drug-testing consortium + take pre-employment test",
        why: "Owner-operators must be in a DOT random pool. The negative pre-employment result goes in your DQ file before you drive under authority.",
        cost: "~$100–150/yr + $60–90 test",
        time: "1 day",
      },
      {
        id: "dq-file",
        title: "Build your Driver Qualification file",
        why: "Application, CDL copy, medical card, MVR, road-test certificate, 3-yr employment history. The new-entrant audit lives and dies on this folder.",
        cost: "Free (MVR ~$10)",
        time: "1 hr",
        link: "https://www.fmcsa.dot.gov/regulations/title49/section/391.51",
        linkLabel: "49 CFR 391.51",
      },
      {
        id: "hvut-2290",
        title: "File IRS Form 2290 (Heavy Vehicle Use Tax)",
        why: "You need the stamped Schedule 1 to get IRP plates. E-file it — the stamp comes back in minutes.",
        cost: "Up to $550/yr",
        time: "30 min",
        link: "https://www.irs.gov/forms-pubs/about-form-2290",
        linkLabel: "IRS Form 2290",
      },
      {
        id: "eld",
        title: "Pick and order a registered ELD",
        why: "Required from day one of interstate hauling. Order now so it's installed before your first load — check the FMCSA registered list, not just ads.",
        cost: "$0–150 device + $20–40/mo",
        time: "1 day",
        link: "https://eld.fmcsa.dot.gov/List",
        linkLabel: "FMCSA registered ELD list",
      },
      {
        id: "ein-bank",
        title: "EIN, business bank account + 6 weeks of cash float",
        why: "Brokers pay net 30–45. Fuel, insurance and the truck note don't wait. Have the float or line up a factoring company now.",
        cost: "Free",
        time: "1 hr",
        link: "https://www.irs.gov/businesses/small-businesses-self-employed/get-an-employer-identification-number-ein-online",
        linkLabel: "Get an EIN (IRS)",
      },
      {
        id: "truck-inspection",
        title: "Truck paperwork + annual DOT inspection",
        why: "Title or lease, bill of sale, VIN, and a current 396.17 annual inspection sticker. Plates can't be issued without clean truck docs.",
        cost: "$100–200 inspection",
        time: "Half a day",
      },
    ],
  },
  {
    id: "mc-day",
    label: "Phase 2",
    title: "The day your MC# posts",
    subtitle: "Everything below happens same-day. The 21-day clock doesn't start until your filings are in.",
    items: [
      {
        id: "boc3",
        title: "File your BOC-3 process agent",
        why: "Required before FMCSA will activate authority. Any process-agent company does it online in minutes.",
        cost: "$25–50 one time",
        time: "10 min",
        link: "https://www.fmcsa.dot.gov/registration/form-boc-3-designation-agents-service-process",
        linkLabel: "About Form BOC-3",
      },
      {
        id: "insurance-filed",
        title: "Bind insurance — insurer files BMC-91X + BMC-34",
        why: "Your agent files liability (BMC-91/91X) and cargo (BMC-34) electronically with FMCSA. Confirm it shows on your record within 48 hrs.",
        cost: "First month + down payment",
        time: "Same day",
        link: "https://li-public.fmcsa.dot.gov/LIVIEW/pkg_carrquery.prc_carrlist",
        linkLabel: "Check filings on FMCSA L&I",
      },
      {
        id: "ucr",
        title: "Register for UCR",
        why: "Unified Carrier Registration is annual and enforced at roadside. 0–2 trucks is the cheapest bracket.",
        cost: "~$40–50/yr",
        time: "10 min",
        link: "https://www.ucr.gov",
        linkLabel: "ucr.gov",
      },
      {
        id: "irp-plates",
        title: "Apply for IRP apportioned plates",
        why: "Your state's IRP office. Bring the stamped 2290, title/lease, proof of business address and insurance.",
        cost: "$1,500–2,500/yr",
        time: "1–5 days",
        link: "https://www.irponline.org",
        linkLabel: "Find your IRP office",
      },
      {
        id: "ifta",
        title: "Get your IFTA license + decals",
        why: "Same office in most states. Two decals per truck, quarterly fuel-tax returns from then on.",
        cost: "~$10 decals",
        time: "Same visit",
        link: "https://www.iftach.org",
        linkLabel: "IFTA jurisdictions",
      },
      {
        id: "brokercheck-self",
        title: "Run your own MC through BrokerCheck",
        why: "See exactly what a broker sees when they vet you — legal name, status, insurance on file. Catch a typo before a broker does.",
        cost: "Free",
        time: "10 sec",
        link: "/broker-check",
        linkLabel: "Open BrokerCheck",
      },
      {
        id: "scam-shield",
        title: "Ignore the 'DOT compliance fee' mailers and calls",
        why: "The week your MC posts, scammers blast new authorities with fake invoices. FMCSA never calls you or mails a bill like that.",
        cost: "Saves $300–800",
        time: "Ongoing",
      },
    ],
  },
  {
    id: "activation",
    label: "Phase 3",
    title: "21-day wait → ACTIVE",
    subtitle: "FMCSA holds a protest window after your filings land. Use it to get load-board ready.",
    items: [
      {
        id: "protest-window",
        title: "Watch your status flip to ACTIVE",
        why: "Check FMCSA L&I every few days. 'Pending' → 'Active' is your green light — you can't legally haul for-hire before that.",
        cost: "Free",
        time: "~21 days",
        link: "https://li-public.fmcsa.dot.gov/LIVIEW/pkg_carrquery.prc_carrlist",
        linkLabel: "FMCSA Licensing & Insurance",
      },
      {
        id: "state-permits",
        title: "State permits for lanes you'll run",
        why: "NY HUT, Kentucky KYU, New Mexico weight-distance, Oregon permit. Only needed if you cross those states — but fines are steep if you skip them.",
        cost: "$0–100 each",
        time: "30 min each",
      },
      {
        id: "carrier-packet",
        title: "Build your carrier packet",
        why: "W-9, COI, authority letter, references, banking or factoring NOA. Brokers ask for it before every first load — keep it one click away.",
        cost: "Free",
        time: "30 min",
        link: "/app/vault",
        linkLabel: "Store it in Docs Vault",
      },
      {
        id: "dat-signup",
        title: "Sign up for DAT (and/or Truckstop) the day you go ACTIVE",
        why: "Load boards verify authority — apply once it shows ACTIVE, under your own company. Most brokers filter authorities under 90 days, so lean on direct relationships early.",
        cost: "From ~$45/mo",
        time: "1 day approval",
        link: "https://www.dat.com",
        linkLabel: "dat.com",
      },
      {
        id: "connect-loadboard",
        title: "Connect DAT / Truckstop to your FreightPilot Load Board",
        why: "Your own company credentials plug straight in — verified freight, broker flags and rate checks in one screen.",
        cost: "Included",
        time: "2 min",
        link: "/app/loadboard",
        linkLabel: "Open Load Board",
      },
    ],
  },
  {
    id: "rolling",
    label: "Phase 4",
    title: "First 30 days rolling",
    subtitle: "Stay audit-ready from load one — the new-entrant audit is coming.",
    items: [
      {
        id: "new-entrant-audit",
        title: "Prep for the New Entrant Safety Audit",
        why: "Happens within your first 12 months. DQ file, HOS logs, maintenance records, drug-testing records and insurance — all organized and current.",
        cost: "Free",
        time: "Ongoing",
        link: "https://www.fmcsa.dot.gov/registration/new-entrant-safety-assurance-program",
        linkLabel: "New Entrant program",
      },
      {
        id: "ifta-quarterly",
        title: "Calendar your first IFTA quarterly return",
        why: "Due Apr 30 / Jul 31 / Oct 31 / Jan 31. Miss one and your license gets revoked. FreightPilot tracks state miles for you.",
        cost: "Fuel tax owed",
        time: "20 min/qtr",
        link: "/app/reports/state-mileage",
        linkLabel: "IFTA Miles report",
      },
      {
        id: "invoicing",
        title: "Set up invoicing + payment reminders",
        why: "Invoice the day you deliver, with BOL attached. Reminders at 30/45/60 days keep the cash coming.",
        cost: "Included",
        time: "10 min",
        link: "/app/invoices",
        linkLabel: "Open Invoices",
      },
      {
        id: "vet-brokers",
        title: "Vet every new broker before you haul",
        why: "BrokerCheck for FMCSA status, Broker Directory for payment reputation. Two minutes now beats 90 days chasing a check.",
        cost: "Free",
        time: "2 min/load",
        link: "/dnp",
        linkLabel: "Open Broker Directory",
      },
      {
        id: "mcs150",
        title: "Calendar your MCS-150 biennial update",
        why: "Every two years, based on your USDOT number. Skipping it deactivates your number — set the reminder now.",
        cost: "Free",
        time: "5 min",
        link: "https://www.fmcsa.dot.gov/registration/updating-your-registration",
        linkLabel: "Updating your registration",
      },
    ],
  },
];

export const AUTHORITY_ITEM_COUNT = AUTHORITY_PHASES.reduce((n, p) => n + p.items.length, 0);
